import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { AnimalWithStats, Feeding, PoopConsistency, PoopColor } from '../data/types';
import './FeedModal.css';

interface FeedModalProps {
    animal: AnimalWithStats | null;
    onClose: () => void;
    onSubmit: (feeding: Feeding) => void;
}

export function FeedModal({ animal, onClose, onSubmit }: FeedModalProps) {
    const [selectedAction, setSelectedAction] = useState<'same' | 'custom'>('same');
    const [customAmount, setCustomAmount] = useState('');
    const [notes, setNotes] = useState('');

    // Elimination tracking
    const [peed, setPeed] = useState(false);
    const [pooped, setPooped] = useState(false);
    const [poopConsistency, setPoopConsistency] = useState<PoopConsistency | null>(null);
    const [poopColor, setPoopColor] = useState<PoopColor | null>(null);

    // Reset form when animal changes
    useEffect(() => {
        if (animal) {
            setSelectedAction('same');
            setCustomAmount('');
            setNotes('');
            setPeed(false);
            setPooped(false);
            setPoopConsistency(null);
            setPoopColor(null);
        }
    }, [animal]);

    if (!animal) return null;

    const lastAmount = animal.lastFeeding?.amountMl || 0;

    const handleSubmit = () => {
        const amount = selectedAction === 'same' ? lastAmount : parseFloat(customAmount);

        if (!amount || amount <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        const feeding: Feeding = {
            id: uuidv4(),
            animalId: animal.id,
            amountMl: amount,
            timestamp: Date.now(),
            notes: notes || undefined,
            createdAt: Date.now(),
            urinated: peed,
            pooped: pooped,
            poopConsistency: pooped ? poopConsistency || undefined : undefined,
            poopColor: pooped ? poopColor || undefined : undefined,
        };

        onSubmit(feeding);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal active" onClick={handleBackdropClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Feed {animal.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {/* Amount Selection */}
                <div className="quick-actions">
                    <div
                        className={`quick-btn ${selectedAction === 'same' ? 'selected' : ''} ${!lastAmount ? 'disabled' : ''}`}
                        onClick={() => lastAmount && setSelectedAction('same')}
                        title={!lastAmount ? 'No previous feeding recorded yet' : `Use last feeding amount (${lastAmount} mL)`}
                        style={!lastAmount ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                    >
                        <div className="quick-btn-icon">⚡</div>
                        <div className="quick-btn-label">Same as Last</div>
                        <div className="quick-btn-detail">{lastAmount ? `${lastAmount} mL` : 'N/A'}</div>
                    </div>
                    <div
                        className={`quick-btn ${selectedAction === 'custom' ? 'selected' : ''}`}
                        onClick={() => setSelectedAction('custom')}
                    >
                        <div className="quick-btn-icon">✏️</div>
                        <div className="quick-btn-label">Custom Amount</div>
                        <div className="quick-btn-detail">Enter manually</div>
                    </div>
                </div>

                {/* Custom Amount Input */}
                {selectedAction === 'custom' && (
                    <div className="form-group">
                        <label className="form-label">
                            Amount <span className="unit">(mL)</span>
                        </label>
                        <input
                            type="number"
                            className="form-input"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder="Enter amount"
                            autoFocus
                        />
                    </div>
                )}

                {/* Elimination Tracking */}
                <div className="elimination-section">
                    <div className="elimination-title">Elimination Check</div>
                    <div className="toggle-group">
                        <div
                            className={`toggle-btn ${peed ? 'active' : ''}`}
                            onClick={() => setPeed(!peed)}
                        >
                            <div className="toggle-icon">💧</div>
                            <div className="toggle-label">Peed</div>
                        </div>
                        <div
                            className={`toggle-btn ${pooped ? 'active' : ''}`}
                            onClick={() => setPooped(!pooped)}
                        >
                            <div className="toggle-icon">💩</div>
                            <div className="toggle-label">Pooped</div>
                        </div>
                    </div>

                    {/* Poop Details */}
                    {pooped && (
                        <div className="poop-details active">
                            <label className="detail-label">Consistency</label>
                            <div className="detail-options">
                                {(['normal', 'soft', 'liquid', 'hard'] as const).map((consistency) => (
                                    <div
                                        key={consistency}
                                        className={`detail-option ${poopConsistency === consistency ? 'selected' : ''}`}
                                        onClick={() => setPoopConsistency(consistency)}
                                    >
                                        {consistency.charAt(0).toUpperCase() + consistency.slice(1)}
                                    </div>
                                ))}
                            </div>

                            <label className="detail-label">Color</label>
                            <div className="detail-options">
                                {(['brown', 'yellow', 'green', 'black', 'bloody'] as const).map((color) => (
                                    <div
                                        key={color}
                                        className={`detail-option ${poopColor === color ? 'selected' : ''}`}
                                        onClick={() => setPoopColor(color)}
                                    >
                                        {color.charAt(0).toUpperCase() + color.slice(1)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Notes */}
                <div className="form-group">
                    <label className="form-label">
                        Notes <span className="unit">(optional)</span>
                    </label>
                    <input
                        type="text"
                        className="form-input"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any notes about this feeding?"
                    />
                </div>

                {/* Submit Button */}
                <button className="submit-btn" onClick={handleSubmit}>
                    Log Feeding
                </button>
            </div>
        </div>
    );
}